import { Platform, UserType } from '@/constants';
import {
    emptyBotPositionPayload,
    emptyOrderPositionPayload,
    emptyToolUserPositionMaps,
    parseToolUserPositions,
    serializeBotPositionPayload,
    serializeOrderPositionPayload,
    type ToolUserPositionMap,
} from '@/helpers/toolUserPositions';
import { AbstractModel } from '@/models/abstractModel';
import type { IToolUserListRowApi, IToolUserSaveRow } from '@/types/toolUser';
import type { ToolUserDetailDocumentsFilterValue } from '@/types/toolUserDetail';

export class ToolUserItem extends AbstractModel {
    public activatedAt: string | null = null;
    public activatedByToolUserId: number | null = null;
    public activatedByToolUserLogin: string | null = null;
    public agentPosition: number | null = null;
    public approvedEmailByToolUserId: number | null = null;
    public approvedEmailByToolUserLogin: string | null = null;
    public approvedEmailMailedAt: string | null = null;
    public betBalanceAmount: string = '0';
    public botPositionPercSite: ToolUserPositionMap = emptyToolUserPositionMaps().bot;
    public botPositionWlPercSite: ToolUserPositionMap = emptyToolUserPositionMaps().botWl;
    public botsActiveCount: number | null = null;
    public botsCount: number = 0;
    public canApprovePlayer: 0 | 1 = 0;
    public commissionBotPerc: string = '';
    public commissionOrderPerc: string = '';
    public companyId: number | null = null;
    public companyPosition: number | null = null;
    public countryId: number | null = null;
    public createdAt: string | null = null;
    public creditAmount: string | null = null;
    public creditLimit: number = 0;
    public currencyId: number | null = null;
    public deactivatedAt: string | null = null;
    public deactivatedByToolUserId: number | null = null;
    public deactivatedByToolUserLogin: string | null = null;
    public deletedAt: string | null = null;
    public description: string | null = null;
    public documentsApproveMailedAt: string | null = null;
    public email: string | null = null;
    public emailVerifiedAt: string | null = null;
    public exposureAmount: string = '0';
    public id: number = 0;
    public isActive: 0 | 1 = 0;
    public isIdExist: 0 | 1 = 0;
    public isIdProof: 0 | 1 = 0;
    public isIrregular: 0 | 1 = 0;
    public isSuspended: 0 | 1 = 0;
    public isSuspicious: 0 | 1 = 0;
    public isUtilityExist: 0 | 1 = 0;
    public isUtilityProof: 0 | 1 = 0;
    public isValidId: 0 | 1 = 0;
    public isValidUtility: 0 | 1 = 0;
    public isWatch: 0 | 1 = 0;
    public lastBetAt: string | null = null;
    public lastLoginAt: string | null = null;
    public login: string = '';
    public name: string | null = null;
    public openedByToolUserId: number | null = null;
    public openedByToolUserLogin: string | null = null;
    public orderPositionBasketballPercSite: ToolUserPositionMap = emptyToolUserPositionMaps().orderBasketball;
    public orderPositionBasketballWlPercSite: ToolUserPositionMap = emptyToolUserPositionMaps().orderBasketballWl;
    public orderPositionPercSite: ToolUserPositionMap = emptyToolUserPositionMaps().orderFootball;
    public orderPositionWlPercSite: ToolUserPositionMap = emptyToolUserPositionMaps().orderFootballWl;
    public ownerId: number | null = null;
    public password: string = '';
    public permission: string | null = null;
    public platformId: Platform = Platform.Tradeball;
    public positionUserRuleId: number | null = null;
    public subusersCount: number = 0;
    public surname: string | null = null;
    public totalBalanceAmount: string = '0';
    public totalBetCount: number = 0;
    public transferBalanceAmount: string = '0';
    public typeId: UserType = UserType.Player;
    public userOwnerLogin: string | null = null;
    public userRefName: string | null = null;
    public verifyEmailByToolUserId: number | null = null;
    public verifyEmailByToolUserLogin: string | null = null;
    public verifyEmailMailedAt: string | null = null;

    constructor(data?: IToolUserListRowApi) {
        super();

        if (!data) {
            return;
        }

        this.id = data.id;
        this.createdAt = data.created_at;
        this.login = data.login;
        this.name = data.name;
        this.surname = data.surname;
        this.email = data.email;
        this.currencyId = data.currency_id;
        this.countryId = data.country_id;
        this.isActive = data.is_active;
        this.isIdProof = data.is_id_proof;
        this.isUtilityProof = data.is_utility_proof;
        this.typeId = data.type_id;
        this.creditLimit = data.credit_limit;
        this.isSuspicious = data.is_suspicious;
        this.ownerId = data.owner_id;
        this.deletedAt = data.deleted_at;
        this.isSuspended = data.is_suspended;
        this.openedByToolUserId = data.opened_by_tool_user_id;
        this.verifyEmailByToolUserId = data.verify_email_by_tool_user_id;
        this.activatedByToolUserId = data.activated_by_tool_user_id;
        this.deactivatedByToolUserId = data.deactivated_by_tool_user_id;
        this.approvedEmailByToolUserId = data.approved_email_by_tool_user_id;
        this.platformId = data.platform_id;
        this.commissionBotPerc = data.commission_bot_perc;
        this.commissionOrderPerc = data.commission_order_perc;
        this.isWatch = data.is_watch;
        this.isIrregular = data.is_irregular;
        this.betBalanceAmount = data.bet_balance_amount;
        this.transferBalanceAmount = data.transfer_balance_amount;
        this.totalBalanceAmount = data.total_balance_amount;
        this.creditAmount = data.credit_amount;
        this.exposureAmount = data.exposure_amount;
        this.lastBetAt = data.last_bet_at;
        this.isValidId = data.is_valid_id;
        this.isIdExist = data.is_id_exist;
        this.isValidUtility = data.is_valid_utility;
        this.isUtilityExist = data.is_utility_exist;
        this.openedByToolUserLogin = data.opened_by_tool_user_login;
        this.verifyEmailByToolUserLogin = data.verify_email_by_tool_user_login;
        this.activatedByToolUserLogin = data.activated_by_tool_user_login;
        this.deactivatedByToolUserLogin = data.deactivated_by_tool_user_login;
        this.approvedEmailByToolUserLogin = data.approved_email_by_tool_user_login;
        this.description = data.description;
        this.permission = data.permission;
        const { bot, orderFootball, orderBasketball, botWl, orderFootballWl, orderBasketballWl } = parseToolUserPositions(data.positions, data.positions_wl);
        this.botPositionPercSite = bot;
        this.orderPositionPercSite = orderFootball;
        this.orderPositionBasketballPercSite = orderBasketball;
        this.botPositionWlPercSite = botWl;
        this.orderPositionWlPercSite = orderFootballWl;
        this.orderPositionBasketballWlPercSite = orderBasketballWl;
        this.positionUserRuleId = data.user_position_rule_id;
        this.companyPosition = data.user_position_rule_company;
        this.agentPosition = data.user_position_rule_agent;
        this.lastLoginAt = data.last_login_at;
        this.password = '';
        this.userRefName = data.user_ref_name;
        this.emailVerifiedAt = data.email_verified_at;
        this.verifyEmailMailedAt = data.verify_email_mailed_at;
        this.activatedAt = data.activated_at;
        this.deactivatedAt = data.deactivated_at;
        this.approvedEmailMailedAt = data.approved_email_mailed_at;
        this.userOwnerLogin = data.user_owner_login;
        this.companyId = data.company_id;
        this.subusersCount = data.subusers_count;
        this.botsCount = data.bots_count;
        this.totalBetCount = data.total_bet_count;
        this.botsActiveCount = data.bots_active_count;
        this.canApprovePlayer = this.parseCanApprovePlayer(data.permission);
        this.documentsApproveMailedAt = data.documents_approve_mailed_at ?? null;
    }

    canManageRowInListSession(myToolUserId: number | null, isSessionAdmin: boolean): boolean {
        if (this.deletedAt) return false;
        if (isSessionAdmin) return true;
        if (myToolUserId == null) return false;
        return this.ownerId === myToolUserId || this.companyId === myToolUserId;
    }

    canShowActiveCheckbox(myToolUserId: number | null, isSessionAdmin: boolean): boolean {
        return this.canManageRowInListSession(myToolUserId, isSessionAdmin);
    }

    canShowPlayerCheckbox(myToolUserId: number | null, isSessionAdmin: boolean): boolean {
        return this.isPlayer() && this.canManageRowInListSession(myToolUserId, isSessionAdmin);
    }

    getDetailsDocumentsStatus(): ToolUserDetailDocumentsFilterValue {
        const idProof = this.isIdProof === 1;
        const utilProof = this.isUtilityProof === 1;
        const idOk = idProof || this.isValidId === 1;
        const utilOk = utilProof || this.isValidUtility === 1;
        if (idProof && utilProof) return 'approved';
        if (idOk && utilOk) return 'approve';
        return 'waiting';
    }

    getDetailsRowClass(): string {
        const parts: string[] = [];
        if (!this.isActive) parts.push('tool-user-row--inactive');
        if (this.isSuspended === 1) parts.push('tool-user-row--suspended');
        return parts.join(' ');
    }

    getListDocumentsCellState(): 'approved' | 'approve' | 'none' {
        if (this.platformId !== Platform.Tradeball) return 'none';
        const idOk = this.isIdProof === 1 || this.isValidId === 1;
        const utilOk = this.isUtilityProof === 1 || this.isValidUtility === 1;
        if (this.isIdProof === 1 && this.isUtilityProof === 1) return 'approved';
        if (idOk && utilOk) return 'approve';
        return 'none';
    }

    getListRowClass(): string {
        const parts: string[] = [];
        if (!this.isActive) parts.push('tool-user-row--inactive');
        if (this.isSuspended === 1) parts.push('tool-user-row--suspended');
        if (this.isSuspicious === 1) parts.push('tool-user-row--suspicious');
        return parts.join(' ');
    }

    isPlayer(): boolean {
        return this.typeId === UserType.Player;
    }

    isWlPlatform(): boolean {
        return this.platformId !== Platform.Tradeball;
    }

    parseCanApprovePlayer(permission: string | null): 0 | 1 {
        if (!permission) return 0;
        try {
            const parsed: unknown = JSON.parse(permission);
            if (Array.isArray(parsed) && parsed.includes('canApprovePlayer')) {
                return 1;
            }
        } catch {
            // ignore malformed JSON
        }
        return 0;
    }

    /** Whether `Position WL %` block must be visible — backend persists data only for non-Tradeball platforms. */
    showWlBlock(): boolean {
        return this.isWlPlatform();
    }

    /**
     * Backend contract for `/api/toolUser/create` and `/api/toolUser/update`:
     *  - flags are sent as `0|1`;
     *  - `currency_id` and `platform_id` are sent on create only — both are immutable afterwards;
     *  - on create, `platform_id` is always Tradeball (agents are created on Tradeball only);
     *  - `opened_by_tool_user_id` is resolved server-side, never echoed back;
     *  - positions are nested `{ sportId: { bookmakerId: percent } }` (bot: football only; order: football + basketball).
     */
    toApiData(): IToolUserSaveRow {
        const isAgent = this.typeId === UserType.Agent;
        const isCreate = !this.id;
        const showWl = this.showWlBlock();

        const row: IToolUserSaveRow = {
            type_id: this.typeId,
            owner_id: this.ownerId || null,
            name: this.name,
            surname: this.surname,
            login: this.login,
            email: this.email,
            country_id: this.countryId,
            description: this.description,
            is_active: this.isActive,
            is_watch: this.isWatch,
            is_irregular: this.isIrregular,
            is_id_proof: this.isIdProof,
            is_utility_proof: this.isUtilityProof,
            credit_limit: this.creditLimit,
            commission_bot_perc: Number(this.commissionBotPerc) || 0,
            commission_order_perc: Number(this.commissionOrderPerc) || 0,
            canApprovePlayer: isAgent && this.canApprovePlayer ? 1 : 0,
            bot_position_perc: serializeBotPositionPayload(this.botPositionPercSite),
            order_position_perc: serializeOrderPositionPayload(this.orderPositionPercSite, this.orderPositionBasketballPercSite),
            bot_position_perc_wl: showWl ? serializeBotPositionPayload(this.botPositionWlPercSite) : emptyBotPositionPayload(),
            order_position_perc_wl: showWl
                ? serializeOrderPositionPayload(this.orderPositionWlPercSite, this.orderPositionBasketballWlPercSite)
                : emptyOrderPositionPayload(),
        };

        if (this.id) {
            row.id = this.id;
        }
        if (isCreate) {
            row.currency_id = this.currencyId;
            row.platform_id = Platform.Tradeball;
        }
        if (this.password) {
            row.password = this.password;
        }
        if (this.typeId === UserType.Player) {
            row.user_position_rule_id = this.positionUserRuleId;
            row.user_position_rule_company = this.positionUserRuleId == null ? null : this.companyPosition;
            row.user_position_rule_agent = this.positionUserRuleId == null ? null : this.agentPosition;
        }
        return row;
    }
}
